import React from 'react';
import HelpArticleLayout, { ArticleH2, Callout, DocName, UiLabel, StatusChip } from '../components/HelpArticleLayout';

// Help article: "What compliance documents do I need?"

export default function HelpRequiredDocuments() {
  return (
    <HelpArticleLayout
      breadcrumb="Compliance documents"
      title="What compliance documents do I need?"
      intro="The documents Fusion needs from you depend on your supplier category and the risk level of the work you do. Your portal home page lists exactly what's required for your business — but here's the full picture."
      footerText="Not sure which documents apply to you? Just ask.">

      <ArticleH2>The short answer</ArticleH2>
      <p>
        Check the <UiLabel>Compliance Documents</UiLabel> section on your portal home. The list there is tailored to your category and risk tier. Anything marked <StatusChip tone="outstanding">Outstanding</StatusChip> needs uploading before Fusion 2K can award you new work.
      </p>

      <ArticleH2>Why some suppliers need more than others</ArticleH2>
      <p className="mb-3.5">
        Fusion groups suppliers into three risk tiers based on the kind of work involved:
      </p>
      <ul className="list-disc pl-6 mb-4 marker:text-fusion-forest space-y-2">
        <li><strong className="text-ink">High Risk</strong> — working at height, electrical installations, high-value structures, anything safety-critical</li>
        <li><strong className="text-ink">Medium Risk</strong> — catering, AV setup, on-site activations, anything happening on the event floor</li>
        <li><strong className="text-ink">Low Risk</strong> — florals, props, talent, retail-style suppliers without complex setup</li>
      </ul>
      <p>
        The higher your risk tier, the more documents you'll need. High-Risk suppliers typically need the full set; Low-Risk suppliers need fewer.
      </p>

      <ArticleH2>Documents most suppliers need</ArticleH2>
      <p className="mb-3.5">These are common across most categories:</p>
      <ul className="list-disc pl-6 mb-4 marker:text-fusion-forest space-y-2">
        <li><DocName>Public Liability Insurance</DocName> — minimum cover varies (£5m is typical, but the exact figure is shown on your portal). Required for nearly all suppliers.</li>
        <li><DocName>Employer's Liability Insurance</DocName> — required if you have staff, even part-time or freelance. Standard minimum is £10m.</li>
        <li><DocName>H&amp;S Policy</DocName> — your business's own health and safety policy. Can be brief; what matters is that you have one.</li>
        <li><DocName>Supplier NDA</DocName> — Fusion 2K's mutual confidentiality agreement. Sign and return.</li>
        <li><DocName>Contractor Code of Conduct</DocName> — read, acknowledge, and return. Sets expectations for on-site behaviour.</li>
      </ul>

      <ArticleH2>Documents you might need depending on your category</ArticleH2>
      <p className="mb-3.5">Some documents are category-specific:</p>
      <ul className="list-disc pl-6 mb-4 marker:text-fusion-forest space-y-2">
        <li><DocName>Food Hygiene Rating</DocName> — for catering suppliers. Latest local authority certificate.</li>
        <li><DocName>PAT Testing Certificate</DocName> — for anyone bringing portable electrical equipment on site.</li>
        <li><DocName>Professional Indemnity Insurance</DocName> — for design, consultancy, and creative suppliers.</li>
        <li><DocName>Method Statements &amp; Risk Assessments</DocName> — for High-Risk suppliers, often required per-event rather than once.</li>
      </ul>

      <Callout tone="danger">
        <strong className="text-ink">Fusion 2K won't award new work to suppliers with outstanding compliance documents.</strong> This isn't admin friction — it's how Fusion 2K keeps the supplier base trustworthy for clients like Renault, Kia and others.
      </Callout>

      <ArticleH2>What if my risk tier seems wrong?</ArticleH2>
      <p>
        Risk tier is set when Fusion onboards you, and it's based on the kind of work you typically deliver. If you think yours is wrong — maybe you've expanded into higher-risk work, or you're being asked for documents that don't apply to your business — drop us a line and we'll review it together.
      </p>

      <ArticleH2>Documents that expire</ArticleH2>
      <p>
        Insurances, food hygiene ratings, and PAT certificates all renew annually (or sometimes every two years). Fusion 2K will email you 30 days before each one expires, with another reminder a week before. You can also see expiry dates on any approved document by clicking <UiLabel>View</UiLabel>.
      </p>
    </HelpArticleLayout>
  );
}
